const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

const toScalarString = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "";
    }

    return value
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item === "object") {
          try {
            return JSON.stringify(item);
          } catch (error) {
            console.warn("Unable to stringify array item", item, error);
            return String(item);
          }
        }
        return String(item);
      })
      .join(" | ");
  }

  if (isPlainObject(value)) {
    try {
      return JSON.stringify(value);
    } catch (error) {
      console.warn("Unable to stringify value", value, error);
      return String(value);
    }
  }

  return String(value);
};

const composeKey = (prefix, key) => (prefix ? `${prefix}.${key}` : key);

const MULTILINE_FIELD_NAMES = new Set(["description", "details"]);

const getTerminalKey = (path = "") => {
  if (!path) {
    return "";
  }
  const segments = String(path).split(".");
  const lastSegment = segments[segments.length - 1];
  return lastSegment.replace(/\[\d+\]$/g, "").toLowerCase();
};

const isMultilineField = (path = "") => MULTILINE_FIELD_NAMES.has(getTerminalKey(path));

const normalizeLineSeparators = (value) =>
  value.replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/\u2028/g, "\n").replace(/\u2029/g, "\n");

const decodeEscapedNewlineSequences = (value) => {
  let decoded = value.replace(/(^|[^\\])\\r\\n/g, (match, prefix) => `${prefix}\n`);
  decoded = decoded.replace(/(^|[^\\])\\n/g, (match, prefix) => `${prefix}\n`);
  decoded = decoded.replace(/(^|[^\\])\\r/g, (match, prefix) => `${prefix}\n`);
  return decoded;
};

const normalizeMultilineText = (value, { decodeEscapedNewlines = false } = {}) => {
  if (typeof value !== "string") {
    return value;
  }

  const hasNativeLineBreaks = /[\r\n\u2028\u2029]/.test(value);
  let normalized = normalizeLineSeparators(value);

  if (decodeEscapedNewlines && !hasNativeLineBreaks) {
    normalized = decodeEscapedNewlineSequences(normalized);
  }

  return normalizeLineSeparators(normalized);
};

const toMultilineCsvValue = (path, value) => {
  if (!isMultilineField(path)) {
    return toScalarString(value);
  }

  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return normalizeMultilineText(value, { decodeEscapedNewlines: true });
  }

  if (Array.isArray(value)) {
    const hasOnlyScalars = value.every(
      (item) => item === null || item === undefined || typeof item !== "object"
    );

    if (hasOnlyScalars) {
      return value
        .map((item) =>
          normalizeMultilineText(item === null || item === undefined ? "" : String(item), {
            decodeEscapedNewlines: true,
          })
        )
        .join("\n");
    }
  }

  return toScalarString(value);
};

const filterRow = (row, excluded = []) => {
  if (!row || typeof row !== "object") return row;
  const filtered = {};
  Object.entries(row).forEach(([key, value]) => {
    const omit = excluded.some((rule) => {
      if (typeof rule === "string") return key === rule;
      if (rule instanceof RegExp) return rule.test(key);
      return false;
    });
    if (!omit) {
      filtered[key] = value;
    }
  });
  return filtered;
};

const applyProjectNames = (row, projectMap = {}) => {
  if (!row || typeof row !== "object" || !projectMap || Object.keys(projectMap).length === 0) {
    return row;
  }

  const mapped = {};
  Object.entries(row).forEach(([key, value]) => {
    if (key === "project" && value && projectMap[value]) {
      mapped[key] = projectMap[value];
    } else {
      mapped[key] = value;
    }
  });
  return mapped;
};

const flattenRecord = (source, prefix = "", target = {}) => {
  if (!isPlainObject(source)) {
    return target;
  }

  Object.entries(source).forEach(([key, value]) => {
    const path = composeKey(prefix, key);

    if (value === null || value === undefined) {
      target[path] = "";
      return;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        target[path] = "";
        return;
      }

      const objectItems = value.filter((item) => isPlainObject(item));

      if (objectItems.length === value.length) {
        value.forEach((item, index) => {
          flattenRecord(item, `${path}[${index}]`, target);
        });
        target[`${path}Count`] = String(value.length);
        return;
      }

      if (objectItems.length > 0) {
        objectItems.forEach((item, index) => {
          flattenRecord(item, `${path}[${index}]`, target);
        });
        const remaining = value
          .filter((item) => !isPlainObject(item))
          .map((item) =>
            normalizeMultilineText(item === null || item === undefined ? "" : String(item), {
              decodeEscapedNewlines: isMultilineField(path),
            })
          );
        if (remaining.length) {
          target[`${path}Values`] = remaining.join(isMultilineField(path) ? "\n" : " | ");
        }
        target[`${path}Count`] = String(value.length);
        return;
      }

      if (isMultilineField(path)) {
        target[path] = value
          .map((item) =>
            normalizeMultilineText(item === null || item === undefined ? "" : String(item), {
              decodeEscapedNewlines: true,
            })
          )
          .join("\n");
        return;
      }

      target[path] = value
        .map((item) => (item === null || item === undefined ? "" : String(item)))
        .join(" | ");
      return;
    }

    if (isPlainObject(value)) {
      flattenRecord(value, path, target);
      return;
    }

    target[path] = toMultilineCsvValue(path, value);
  });

  return target;
};

const escapeCsvCell = (value) => {
  let stringValue = value === null || value === undefined ? "" : String(value);
  if (typeof value === "string") {
    const trimmed = value.trimStart();
    const firstChar = trimmed.charAt(0);
    const startsWithFormulaToken =
      firstChar === "=" || firstChar === "+" || firstChar === "-" || firstChar === "@";
    const isNumericLiteral = /^[-+]?(?:\d+|\d*\.\d+)(?:[eE][-+]?\d+)?$/.test(trimmed);

    if (startsWithFormulaToken && !(isNumericLiteral && (firstChar === "+" || firstChar === "-"))) {
      stringValue = `'${stringValue}`;
    }
  }

  if (
    stringValue.includes(",") ||
    stringValue.includes("\n") ||
    stringValue.includes("\r") ||
    stringValue.includes('"')
  ) {
    return '"' + stringValue.replace(/"/g, '""') + '"';
  }
  return stringValue;
};

const naturalCompare = (a, b) => {
  const aStr = a === null || a === undefined ? "" : String(a);
  const bStr = b === null || b === undefined ? "" : String(b);
  return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: "base" });
};

const orderHeaders = (headers, preferredHeaders = []) => {
  const headerSet = new Set(headers);
  const preferredSet = new Set(preferredHeaders);
  const orderedPreferred = preferredHeaders.filter((header) => headerSet.has(header));
  const remainingHeaders = headers.filter((header) => !preferredSet.has(header));
  remainingHeaders.sort(naturalCompare);
  return [...orderedPreferred, ...remainingHeaders];
};

const COMMON_EXCLUDED_COLUMNS = ["customId", "color", "dateCreated", "dateModified"];
const DEWAR_EXCLUDED_COLUMNS = [...COMMON_EXCLUDED_COLUMNS];
const RACK_EXCLUDED_COLUMNS = ["productCustomId", "rackSerialNo", ...COMMON_EXCLUDED_COLUMNS];
const PUCK_EXCLUDED_COLUMNS = [
  "productCustomId",
  "rackSerialNo",
  "gridNumbering",
  ...COMMON_EXCLUDED_COLUMNS,
  /^grid(TopLeft|TopRight|BottomLeft|BottomRight)\./i,
];
const WELL_EXCLUDED_COLUMNS = [
  "productCustomId",
  "rackSerialNo",
  "gridNumbering",
  ...COMMON_EXCLUDED_COLUMNS,
  /^grid(TopLeft|TopRight|BottomLeft|BottomRight)\./i,
];

const CSV_HEADER_PREFERENCES = {
  dewars: ["title", "description", "project", "capacity", "rackCount"],
  racks: ["serialNo", "title", "description", "project", "rackType", "productTitle", "puckCount"],
  pucks: ["serialNo", "title", "description", "project", "puckType", "wellCount", "wellPositions"],
  wells: ["puckSerialNo", "well", "title", "description", "project", "boxType", "gridNumbering"],
};

const buildCsvContent = (rows, preferredHeaders = []) => {
  if (!rows || rows.length === 0) {
    return "";
  }

  const discoveredHeaders = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row).forEach((key) => set.add(key));
      return set;
    }, new Set())
  );
  const headers = orderHeaders(discoveredHeaders, preferredHeaders);

  const csvLines = [headers.join(",")];

  rows.forEach((row) => {
    const cells = headers.map((header) => escapeCsvCell(row[header]));
    csvLines.push(cells.join(","));
  });

  return csvLines.join("\r\n");
};

export const buildProductCsvExports = (products = [], options = {}) => {
  const { projectMap = {} } = options;
  const dewars = [];
  const racks = [];
  const pucks = [];
  const wells = [];

  products.forEach((product) => {
    if (!isPlainObject(product)) {
      return;
    }

    const { racks: rackList = [], details, ...dewarFields } = product;
    const dewarRow = applyProjectNames(flattenRecord(dewarFields, "", {}), projectMap);
    if (details !== undefined) {
      dewarRow.description = toMultilineCsvValue("description", details);
    }
    dewarRow.rackCount = toScalarString(Array.isArray(rackList) ? rackList.length : 0);
    dewars.push(filterRow(dewarRow, DEWAR_EXCLUDED_COLUMNS));

    if (!Array.isArray(rackList)) {
      return;
    }

    const sortedRackList = [...rackList].sort((a, b) =>
      naturalCompare(a?.serialNo, b?.serialNo)
    );

    sortedRackList.forEach((rack) => {
      if (!isPlainObject(rack)) {
        return;
      }

      const { pucks: puckList = [], ...rackFields } = rack;
      const rackRow = applyProjectNames({
        productCustomId: toScalarString(product.customId),
        productTitle: toScalarString(product.title),
        ...flattenRecord(rackFields, "", {}),
        puckCount: toScalarString(Array.isArray(puckList) ? puckList.length : 0),
      }, projectMap);
      racks.push(filterRow(rackRow, RACK_EXCLUDED_COLUMNS));

      if (!Array.isArray(puckList)) {
        return;
      }

      const sortedPuckList = [...puckList].sort((a, b) =>
        naturalCompare(a?.serialNo, b?.serialNo)
      );

      sortedPuckList.forEach((puck) => {
        if (!isPlainObject(puck)) {
          return;
        }

        const nonWellFields = {};
        const wellPositions = [];

        const wellEntries = Object.entries(puck).filter(([key, value]) => {
          const lowerKey = key.toLowerCase();
          return lowerKey.startsWith("well") && isPlainObject(value);
        });

        wellEntries
          .sort(([keyA], [keyB]) => {
            const numA = Number(keyA.replace(/\D+/g, "")) || 0;
            const numB = Number(keyB.replace(/\D+/g, "")) || 0;
            return numA - numB || naturalCompare(keyA, keyB);
          })
          .forEach(([key, value]) => {
            wellPositions.push(key);
            const wellRow = applyProjectNames(
              {
                productCustomId: toScalarString(product.customId),
                rackSerialNo: toScalarString(rack.serialNo),
                puckSerialNo: toScalarString(puck.serialNo),
                well: key,
                ...flattenRecord(value, "", {}),
              },
              projectMap
            );
            wells.push(filterRow(wellRow, WELL_EXCLUDED_COLUMNS));
          });

        Object.entries(puck).forEach(([key, value]) => {
          const lowerKey = key.toLowerCase();
          const isWell = lowerKey.startsWith("well");

          if (!(isWell && isPlainObject(value))) {
            nonWellFields[key] = value;
          }
        });

        const puckRow = applyProjectNames({
          productCustomId: toScalarString(product.customId),
          rackSerialNo: toScalarString(rack.serialNo),
          ...flattenRecord(nonWellFields, "", {}),
        }, projectMap);

        if (wellPositions.length) {
          puckRow.wellCount = String(wellPositions.length);
          puckRow.wellPositions = wellPositions.join(", ");
        }

        pucks.push(filterRow(puckRow, PUCK_EXCLUDED_COLUMNS));
      });
    });
  });

  return [
    {
      name: "dewars",
      rows: dewars,
      content: buildCsvContent(dewars, CSV_HEADER_PREFERENCES.dewars),
    },
    {
      name: "racks",
      rows: racks,
      content: buildCsvContent(racks, CSV_HEADER_PREFERENCES.racks),
    },
    {
      name: "pucks",
      rows: pucks,
      content: buildCsvContent(pucks, CSV_HEADER_PREFERENCES.pucks),
    },
    {
      name: "wells",
      rows: wells,
      content: buildCsvContent(wells, CSV_HEADER_PREFERENCES.wells),
    },
  ];
};


export { buildProductHtmlViewer } from "./productHtmlExport.js";
